'use client';

import { Button, Entering, Typography } from 'components';

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AccordionUI
} from 'components/AccordionUI';
import { ArrowRightIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useGithubData } from 'providers/IntegrationsProvider/useGithubData';
import Github from './_components/Github';

export default function IntegrationsPage() {
  const { data } = useGithubData();
  const t = useTranslations();

  const openSettingsTokens = () => window.open('https://github.com/settings/tokens', '_blank');

  return (
    <div className="flex">
      <div className="mb-8 flex gap-8 pt-10 md:mx-4 lg:mx-8">
        <div className="w-[70%]">
          <div className="mb-2 flex items-center justify-between">
            <Entering offset={5} className="flex" position="top">
              <Link href="/integrations">
                <Button
                  variant="icon"
                  size="medium"
                  colorIcon="blue"
                  className="mr-2 flex items-center !shadow-none"
                  startAdornment={
                    <ArrowRightIcon className="rotate-180 scale-150 !text-secondary-link" />
                  }
                />
              </Link>
              <Typography variant="medium4xl" className="!mt-0 flex text-tertiary-100">
                {t('integrations.github.integration.title')}
              </Typography>
            </Entering>
          </div>
          <div className="mb-4">
            <Typography variant="light4" className="!mt-0 flex text-tertiary-100">
              {t('integrations.github.integration.copy1')}
            </Typography>
          </div>
          <div className="mb-4">
            <Typography variant="light4" className="!mt-0 flex text-tertiary-100">
              {t.raw('integrations.github.integration.copy2')}
            </Typography>
          </div>
          <div className="mb-6 w-fit rounded-md border border-tertiary-500 bg-white px-4 py-2">
            <div className="mb-2">
              <Typography variant="medium4" className="!mt-0 flex text-tertiary-100">
                {t.raw('integrations.github.integration.copy3')}
              </Typography>
            </div>
            <ul className="list-disc pl-6 text-[9px]">
              <li>
                <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                  {t('integrations.github.integration.copy4')}
                </Typography>
              </li>
              <li>
                <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                  {t('integrations.github.integration.copy5')}
                </Typography>
              </li>
              <li>
                <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                  {t('integrations.github.integration.copy6')}
                </Typography>
              </li>
            </ul>
          </div>
          <AccordionUI type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="!text-2xl">
                {t('integrations.github.user.title')}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-2 px-4">
                <div className="pt-2">
                  <Typography variant="light4" className={'text-tertiary-100'}>
                    {t('integrations.github.user.copy1')}
                  </Typography>
                  <Typography
                    variant="regular4"
                    className={'ml-1 cursor-pointer text-secondary-link'}
                    align="left"
                    onClick={openSettingsTokens}
                  >
                    {t('auth.two_factor.click_here')}
                  </Typography>
                  <Typography variant="light4" className={'text-tertiary-100'}>
                    .
                  </Typography>
                </div>
                <Typography variant="light4" className={'text-tertiary-100'}>
                  <span
                    dangerouslySetInnerHTML={{ __html: t.raw('integrations.github.user.copy2') }}
                  />
                </Typography>
                <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                  {t('integrations.github.user.copy3')}
                </Typography>
                <Image
                  src="/images/tutorial/repo_config.png"
                  alt="Personal access token repository config"
                  width={825}
                  height={372}
                />
                <div className="mt-2">
                  <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                    {t('integrations.github.user.copy4')}
                  </Typography>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="!text-2xl">
                {t('integrations.github.webhook.title')}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-2 px-4">
                <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                  {t('integrations.github.webhook.copy1')}
                </Typography>
                <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                  <span
                    className="!font-content !text-lg !font-light !text-tertiary-100"
                    dangerouslySetInnerHTML={{
                      __html: t.raw('integrations.github.webhook.copy2')
                    }}
                  />
                </Typography>
                <Image
                  src="/images/tutorial/webhook_config.png"
                  alt="Webhook configuration"
                  width={825}
                  height={433}
                />
                <div className="mt-2">
                  <Typography variant="light4" className="!m-0 !mb-2 flex text-tertiary-100">
                    <span
                      className="!font-content !text-lg !font-light !text-tertiary-100"
                      dangerouslySetInnerHTML={{
                        __html: t.raw('integrations.github.webhook.copy3')
                      }}
                    />
                  </Typography>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="!text-2xl">
                {t('integrations.github.tolq_file.title')}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-2 px-4">
                <div className="legacy">
                  <div className="github">
                    <div className="gihub__content">
                      <p
                        className="!font-content !text-lg !font-light !text-tertiary-100"
                        dangerouslySetInnerHTML={{
                          __html: t.raw('integrations.github.tolq_file.copy1')
                        }}
                      />
                      <p
                        className="!font-content !text-lg !font-light !text-tertiary-100"
                        dangerouslySetInnerHTML={{
                          __html: t.raw('integrations.github.tolq_file.copy2')
                        }}
                      />
                      <pre>
                        <div>
                          <span>tolq_auto_client_review</span>: true
                        </div>
                        <div>
                          <span>files</span>: - <span>source_file</span>: config/locales/en.yml
                        </div>
                        <div>
                          <span>source_language</span>: en
                        </div>
                        <div>
                          <span>target_languages</span>: - <span>language</span>: nl
                        </div>
                        <div>
                          <span>target_file</span>: config/locales/nl.yml
                        </div>
                        <div>
                          <span>quality</span>: localization - <span>language</span>: de
                        </div>
                        <div>
                          <span>target_file</span>: config/locales/de.yml
                        </div>
                        <div>
                          <span>quality</span>: localization
                        </div>
                      </pre>
                      <p
                        className="!font-content !text-lg !font-light !text-tertiary-100"
                        dangerouslySetInnerHTML={{
                          __html: t.raw('integrations.github.tolq_file.copy3')
                        }}
                      />
                      <p
                        className="!mb-1 !font-content !text-lg !font-light !text-tertiary-100"
                        dangerouslySetInnerHTML={{
                          __html: t.raw('integrations.github.tolq_file.copy4')
                        }}
                      />
                      <p className="!mb-1 !font-content !text-lg !font-light !text-tertiary-100">
                        {data?.orderableQualities?.map((quality) => (
                          <code key={quality} className="mr-2">
                            {quality}
                          </code>
                        ))}
                      </p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </AccordionUI>
        </div>
        <div className="w-[30%]">
          <Github />
        </div>
      </div>
    </div>
  );
}
